import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Bell, X, CheckCircle, AlertCircle, Info, AlertTriangle, RefreshCw, XCircle } from 'lucide-react';
import { enhancedApi } from '../services/api';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'assignment' | 'audit_assigned' | 'audit_submitted' | 'audit_approved' | 'audit_rejected' | 'audit_revision_requested' | 'due_reminder' | 'overdue' | 'escalation' | 'team_update' | 'system_maintenance' | 'policy_update' | 'performance_reminder' | 'compliance_alert' | 'bulk_assignment';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  is_read?: boolean; // Backend field
  actionUrl?: string;
  actionText?: string;
  assignmentId?: number;
  caseType?: 'mortality' | 'morbidity';
  priority?: 'low' | 'medium' | 'high' | 'critical';
  data?: any; // Backend data field
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  addAssignmentNotification: (assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    assignedTo: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }) => void;
  addDueReminderNotification: (assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    daysUntilDue: number;
  }) => void;
  addOverdueNotification: (assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    daysOverdue: number;
  }) => void;
  addRevisionNotification: (revisionData: {
    auditId: number;
    reviewerName: string;
    reviewNotes: string;
    revisionCount: number;
  }) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await enhancedApi.notifications.getNotifications({ page: 1, limit: 50 });
      
      if (response.success && response.data) {
        const apiNotifications = response.data.notifications.map((notif: any) => ({
          id: notif.id.toString(),
          type: notif.type,
          title: notif.title,
          message: notif.message,
          timestamp: new Date(notif.created_at),
          read: notif.is_read || false,
          is_read: notif.is_read || false,
          data: notif.data,
          actionUrl: notif.action_url || undefined,
          actionText: notif.action_text || undefined,
          assignmentId: notif.data?.assignment_id,
          caseType: notif.data?.case_type,
          priority: notif.data?.priority
        }));
        
        setNotifications(apiNotifications);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const addNotification = useCallback((notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
      read: false,
    };

    setNotifications(prev => [newNotification, ...prev]);
  }, []);

  const addAssignmentNotification = useCallback((assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    assignedTo: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }) => {
    addNotification({
      type: 'assignment',
      title: 'New Audit Assignment',
      message: `You have been assigned a ${assignmentData.caseType} case #${assignmentData.caseId}. Due: ${new Date(assignmentData.dueDate).toLocaleDateString()}`,
      actionUrl: `/audit/${assignmentData.assignmentId}`,
      actionText: 'View Assignment',
      assignmentId: assignmentData.assignmentId,
      caseType: assignmentData.caseType,
      priority: assignmentData.priority
    });
  }, [addNotification]);

  const addDueReminderNotification = useCallback((assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    daysUntilDue: number;
  }) => {
    addNotification({
      type: 'due_reminder',
      title: 'Assignment Due Soon',
      message: `${assignmentData.caseType} case #${assignmentData.caseId} is due in ${assignmentData.daysUntilDue} day${assignmentData.daysUntilDue > 1 ? 's' : ''}`,
      actionUrl: `/audit/${assignmentData.assignmentId}`,
      actionText: 'View Assignment',
      assignmentId: assignmentData.assignmentId,
      caseType: assignmentData.caseType
    });
  }, [addNotification]);

  const addOverdueNotification = useCallback((assignmentData: {
    assignmentId: number;
    caseType: 'mortality' | 'morbidity';
    caseId: number;
    daysOverdue: number;
  }) => {
    addNotification({
      type: 'overdue',
      title: 'Assignment Overdue',
      message: `${assignmentData.caseType} case #${assignmentData.caseId} is ${assignmentData.daysOverdue} day${assignmentData.daysOverdue > 1 ? 's' : ''} overdue`,
      actionUrl: `/audit/${assignmentData.assignmentId}`,
      actionText: 'View Assignment',
      assignmentId: assignmentData.assignmentId,
      caseType: assignmentData.caseType
    });
  }, [addNotification]);

  const addRevisionNotification = useCallback((revisionData: {
    auditId: number;
    reviewerName: string;
    reviewNotes: string;
    revisionCount: number;
  }) => {
    addNotification({
      type: 'audit_revision_requested',
      title: 'Audit Revision Required',
      message: `${revisionData.reviewerName} has requested revision #${revisionData.revisionCount} for your audit. ${revisionData.reviewNotes}`,
      actionUrl: `/audit/${revisionData.auditId}`,
      actionText: 'Review & Revise',
      assignmentId: revisionData.auditId
    });
  }, [addNotification]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      // Update local state immediately
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, read: true, is_read: true } : notification
        )
      );
      
      // Sync with backend
      await enhancedApi.notifications.markAsRead(parseInt(id));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Revert local state on error
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === id ? { ...notification, read: false, is_read: false } : notification
        )
      );
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      // Update local state immediately
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true, is_read: true }))
      );
      
      // Sync with backend
      await enhancedApi.notifications.markAllAsRead();
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      // Revert local state on error
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: false, is_read: false }))
      );
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        addNotification,
        addAssignmentNotification,
        addDueReminderNotification,
        addOverdueNotification,
        addRevisionNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Toast Notification Component
export const ToastNotification: React.FC<{ notification: Notification; onClose: () => void }> = ({
  notification,
  onClose,
}) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-success-600" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-danger-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-info-600" />;
      case 'assignment':
        return <Bell className="h-5 w-5 text-blue-600" />;
      case 'audit_assigned':
        return <Bell className="h-5 w-5 text-green-600" />;
      case 'audit_submitted':
        return <Bell className="h-5 w-5 text-blue-600" />;
      case 'audit_approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'audit_rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'due_reminder':
        return <AlertTriangle className="h-5 w-5 text-orange-600" />;
      case 'overdue':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'audit_revision_requested':
        return <RefreshCw className="h-5 w-5 text-amber-600" />;
      case 'escalation':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'team_update':
        return <Bell className="h-5 w-5 text-purple-600" />;
      case 'system_maintenance':
        return <AlertCircle className="h-5 w-5 text-orange-600" />;
      case 'policy_update':
        return <Info className="h-5 w-5 text-blue-600" />;
      case 'performance_reminder':
        return <CheckCircle className="h-5 w-5 text-indigo-600" />;
      case 'compliance_alert':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Info className="h-5 w-5 text-info-600" />;
    }
  };

  const getBgColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-success-50 border-success-200';
      case 'error':
        return 'bg-danger-50 border-danger-200';
      case 'warning':
        return 'bg-warning-50 border-warning-200';
      case 'info':
        return 'bg-info-50 border-info-200';
      case 'assignment':
        return 'bg-blue-50 border-blue-200';
      case 'audit_assigned':
        return 'bg-green-50 border-green-200';
      case 'audit_submitted':
        return 'bg-blue-50 border-blue-200';
      case 'audit_approved':
        return 'bg-green-50 border-green-200';
      case 'audit_rejected':
        return 'bg-red-50 border-red-200';
      case 'due_reminder':
        return 'bg-orange-50 border-orange-200';
      case 'overdue':
        return 'bg-red-50 border-red-200';
      case 'audit_revision_requested':
        return 'bg-amber-50 border-amber-200';
      case 'escalation':
        return 'bg-red-50 border-red-200';
      case 'team_update':
        return 'bg-purple-50 border-purple-200';
      case 'system_maintenance':
        return 'bg-orange-50 border-orange-200';
      case 'policy_update':
        return 'bg-blue-50 border-blue-200';
      case 'performance_reminder':
        return 'bg-indigo-50 border-indigo-200';
      case 'compliance_alert':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-info-50 border-info-200';
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white rounded-xl shadow-medical-lg border ${getBgColor()} p-4 transform transition-all duration-300 ease-in-out`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {getIcon()}
        </div>
        <div className="ml-3 flex-1">
          <h4 className="text-sm font-semibold text-gray-900">{notification.title}</h4>
          <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
          {notification.actionUrl && notification.actionText && (
            <a
              href={notification.actionUrl}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-2 inline-block"
            >
              {notification.actionText}
            </a>
          )}
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Center Component
export const NotificationCenter: React.FC<{ isOpen: boolean; onClose: () => void }> = ({
  isOpen,
  onClose,
}) => {
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead, removeNotification, clearAll, refreshNotifications } = useNotifications();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center">
              <Bell className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <button
                onClick={markAllAsRead}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Mark all as read
              </button>
              <button
                onClick={refreshNotifications}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium"
              >
                Refresh
              </button>
            </div>
            <button
              onClick={clearAll}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium"
            >
              Clear all
            </button>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-lg font-medium">Loading notifications...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <AlertCircle className="h-12 w-12 text-red-300 mb-4" />
                <p className="text-lg font-medium">Error loading notifications</p>
                <p className="text-sm">{error}</p>
                <button
                  onClick={refreshNotifications}
                  className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Try Again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Bell className="h-12 w-12 text-gray-300 mb-4" />
                <p className="text-lg font-medium">No notifications</p>
                <p className="text-sm">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-primary-50' : ''
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900">
                            {notification.title}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">
                              {notification.timestamp.toLocaleTimeString()}
                            </span>
                            {!notification.read && (
                              <div className="w-2 h-2 bg-primary-600 rounded-full" />
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        {notification.actionUrl && notification.actionText && (
                          <a
                            href={notification.actionUrl}
                            className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-2 inline-block"
                            onClick={() => markAsRead(notification.id)}
                          >
                            {notification.actionText}
                          </a>
                        )}
                        {notification.data && (
                          <div className="mt-2 text-xs text-gray-500">
                            {notification.data.case_type && (
                              <span className="inline-block bg-gray-100 px-2 py-1 rounded mr-2">
                                {notification.data.case_type}
                              </span>
                            )}
                            {notification.data.priority && (
                              <span className={`inline-block px-2 py-1 rounded ${
                                notification.data.priority === 'high' ? 'bg-red-100 text-red-800' :
                                notification.data.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {notification.data.priority}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeNotification(notification.id)}
                        className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

